import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class PalletType extends ModelObjectWithDirtyCheck<PalletType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Pallet type name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Whether this is the default pallet type. There can be 0 or 1 default pallet types.
    */
    public isDefault: boolean = false;
    public isHidden: boolean = false;
    /**
    * Weight of this type of pallet when empty.
    * Not Negative
    */
    public emptyWeightKg: number = 0;
    private _eprBomId: Guid | null = null;
    /**
    * The packaging BOM to be used for all pallets that are assigned to this pallet type.
    */
    public get eprBomId(): Guid | null { return this._eprBomId }
    public set eprBomId(value: Guid | null) {
      this._eprBomId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isDefault = jsonSerializedObject.isDefault;
          this.isHidden = jsonSerializedObject.isHidden;
          this.emptyWeightKg = jsonSerializedObject.emptyWeightKg;
          this.eprBomId = jsonSerializedObject.eprBomId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.eprBomId);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isDefault = this.isDefault;
        toRet.isHidden = this.isHidden;
        toRet.emptyWeightKg = this.emptyWeightKg;
        toRet.eprBomId = this.eprBomId == null ? null : this.eprBomId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "PalletType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "eprBomId", rule: (v: any) => v == null || !v.isEmpty() || "PalletType.EprBomId cannot be empty"},
    ];
}


export class PalletTypeService extends ServiceBase {

  public async getAllPalletTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PalletType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.palletTypes.map((v: any) => new PalletType(v));;
    return this.makeJsonRequest("palletType/v1/getAllPalletTypes", req, false, extractor, opts);
  };

  public async getPalletType(palletTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PalletType> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletTypeId:palletTypeId.toString(),
    };
    const extractor = (response:any) => new PalletType(response.palletType);;
    return this.makeJsonRequest("palletType/v1/getPalletType", req, false, extractor, opts);
  };

  public async getPalletTypes(palletTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PalletType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletTypeIds:palletTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.palletTypes.map((v: any) => new PalletType(v));;
    return this.makeJsonRequest("palletType/v1/getPalletTypes", req, false, extractor, opts);
  };

  public async savePalletType(palletType: PalletType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletType:palletType.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("palletType/v1/savePalletType", req, true, extractor, opts);
  };

  public async setPalletTypeIsHidden(palletTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    palletTypeId:palletTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("palletType/v1/setPalletTypeIsHidden", req, true, extractor, opts);
  };
}

export const palletTypeService = new PalletTypeService();